// src/pages/business/bookings/BookingDetails.tsx
// Web adaptation of mobile BookingDetailScreen

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, User, Phone, Calendar, Clock, CreditCard,
  CheckCircle, XCircle, Loader2, AlertCircle, UserCheck
} from 'lucide-react';
import apiClient from '@/services/apiClient';
import { useToast } from '@/contexts/ToastContext';

type BookingStatus = 'pending' | 'confirmed' | 'completed' | 'cancelled';

interface BookingDetail {
  id: string;
  service_name: string;
  customer_name: string;
  customer_phone: string;
  booking_date: string;
  booking_time: string;
  status: BookingStatus;
  notes?: string;
  staff_name?: string;
  service_price?: number;
  created_at: string;
  updated_at: string;
}

const statusConfig: Record<BookingStatus, { label: string; color: string; bgColor: string }> = {
  pending: { label: 'Pending', color: '#F59E0B', bgColor: 'rgba(245,158,11,0.12)' },
  confirmed: { label: 'Confirmed', color: '#3B82F6', bgColor: 'rgba(59,130,246,0.12)' },
  completed: { label: 'Completed', color: '#10B981', bgColor: 'rgba(16,185,129,0.12)' },
  cancelled: { label: 'Cancelled', color: '#EF4444', bgColor: 'rgba(239,68,68,0.12)' },
};

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const BookingDetails: React.FC = () => {
  const { businessId, bookingId } = useParams<{ businessId: string; bookingId: string }>();
  const navigate = useNavigate();
  const { success: toastSuccess, error: toastError } = useToast();

  const [booking, setBooking] = useState<BookingDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchBookingDetails();
  }, [businessId, bookingId]);

  const fetchBookingDetails = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await apiClient.get(`/businesses/${businessId}/bookings/${bookingId}`);
      setBooking(data.booking);
    } catch (err: any) {
      console.error('Failed to fetch booking details:', err);
      setError(err.response?.data?.error || 'Failed to load booking details');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (newStatus: BookingStatus) => {
    if (!window.confirm(`Mark this booking as ${statusConfig[newStatus].label}?`)) {
      return;
    }

    setUpdating(true);
    try {
      await apiClient.patch(`/businesses/${businessId}/bookings/${bookingId}/status`, {
        status: newStatus,
      });
      fetchBookingDetails();
      toastSuccess(`Booking has been ${statusConfig[newStatus].label.toLowerCase()}`);
    } catch (err: any) {
      console.error('Failed to update status:', err);
      toastError(err.response?.data?.error || 'Failed to update booking status');
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-light-bg dark:bg-dark-bg flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 size={40} className="animate-spin" style={{ color: '#4F46E5' }} />
          <p className="text-sm text-light-subtle dark:text-dark-subtle">Loading booking details...</p>
        </div>
      </div>
    );
  }

  if (error || !booking) {
    return (
      <div className="min-h-screen bg-light-bg dark:bg-dark-bg flex items-center justify-center">
        <div className="flex flex-col items-center gap-4 text-center p-8">
          <AlertCircle size={48} style={{ color: '#EF4444' }} />
          <h2 className="text-xl font-bold text-light-text dark:text-dark-text">Booking Not Found</h2>
          <p className="text-light-subtle dark:text-dark-subtle">
            {error || 'The requested booking could not be found.'}
          </p>
          <button
            onClick={() => navigate(`/business/${businessId}/bookings`)}
            className="mt-4 px-6 py-3 rounded-xl text-white font-semibold transition-colors"
            style={{ backgroundColor: '#4F46E5' }}
          >
            Go Back to Bookings
          </button>
        </div>
      </div>
    );
  }

  const currentStatus = statusConfig[booking.status];
  const showActions = booking.status === 'pending' || booking.status === 'confirmed';

  return (
    <div className="min-h-screen bg-light-bg dark:bg-dark-bg pb-24">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-light-card dark:bg-dark-card border-b border-light-border dark:border-dark-border">
        <div className="max-w-5xl mx-auto flex items-center justify-between px-4 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(`/business/${businessId}/bookings`)}
              className="p-2 rounded-lg hover:bg-light-bg dark:hover:bg-dark-bg transition-colors"
            >
              <ArrowLeft size={24} style={{ color: '#4F46E5' }} />
            </button>
            <div>
              <p className="font-bold text-lg text-light-text dark:text-dark-text">{booking.service_name}</p>
              <p className="text-xs text-light-subtle dark:text-dark-subtle">
                {formatDate(booking.booking_date)} at {booking.booking_time}
              </p>
            </div>
          </div>
          <span
            className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold"
            style={{ backgroundColor: currentStatus.bgColor, color: currentStatus.color }}
          >
            {currentStatus.label}
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        {/* Appointment Status */}
        <div className="bg-light-card dark:bg-dark-card rounded-xl p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-sm text-light-text dark:text-dark-text">Appointment Status</h3>
            <span
              className="px-4 py-2 rounded-full text-sm font-semibold"
              style={{ backgroundColor: currentStatus.bgColor, color: currentStatus.color }}
            >
              {currentStatus.label}
            </span>
          </div>
        </div>

        {/* Client & Service Information */}
        <div className="bg-light-card dark:bg-dark-card rounded-xl p-5 shadow-sm">
          <h3 className="font-semibold text-sm mb-4 text-light-text dark:text-dark-text">Client & Service</h3>
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(79,70,229,0.12)' }}>
                <User size={20} style={{ color: '#4F46E5' }} />
              </div>
              <div>
                <p className="font-medium text-sm text-light-text dark:text-dark-text">{booking.customer_name}</p>
                <p className="text-xs text-light-subtle dark:text-dark-subtle">Client Name</p>
              </div>
            </div>

            {booking.customer_phone && (
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(79,70,229,0.12)' }}>
                  <Phone size={18} style={{ color: '#4F46E5' }} />
                </div>
                <div>
                  <p className="font-medium text-sm text-light-text dark:text-dark-text">{booking.customer_phone}</p>
                  <p className="text-xs text-light-subtle dark:text-dark-subtle">Contact</p>
                </div>
              </div>
            )}

            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(79,70,229,0.12)' }}>
                <CreditCard size={18} style={{ color: '#4F46E5' }} />
              </div>
              <div>
                <p className="font-medium text-sm text-light-text dark:text-dark-text">{booking.service_name}</p>
                <p className="text-xs text-light-subtle dark:text-dark-subtle">Service</p>
              </div>
            </div>

            {booking.service_price && (
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(16,185,129,0.12)' }}>
                  <CreditCard size={18} style={{ color: '#10B981' }} />
                </div>
                <div>
                  <p className="font-medium text-sm text-light-text dark:text-dark-text">KES {booking.service_price.toLocaleString()}</p>
                  <p className="text-xs text-light-subtle dark:text-dark-subtle">Price</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Schedule & Staff */}
        <div className="bg-light-card dark:bg-dark-card rounded-xl p-5 shadow-sm">
          <h3 className="font-semibold text-sm mb-4 text-light-text dark:text-dark-text">Schedule & Staff</h3>
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(79,70,229,0.12)' }}>
                <Calendar size={18} style={{ color: '#4F46E5' }} />
              </div>
              <div>
                <p className="font-medium text-sm text-light-text dark:text-dark-text">{formatDate(booking.booking_date)}</p>
                <p className="text-xs text-light-subtle dark:text-dark-subtle">Date</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(79,70,229,0.12)' }}>
                <Clock size={18} style={{ color: '#4F46E5' }} />
              </div>
              <div>
                <p className="font-medium text-sm text-light-text dark:text-dark-text">{booking.booking_time}</p>
                <p className="text-xs text-light-subtle dark:text-dark-subtle">Time</p>
              </div>
            </div>

            {booking.staff_name && (
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(139,92,246,0.12)' }}>
                  <UserCheck size={18} style={{ color: '#8B5CF6' }} />
                </div>
                <div>
                  <p className="font-medium text-sm text-light-text dark:text-dark-text">{booking.staff_name}</p>
                  <p className="text-xs text-light-subtle dark:text-dark-subtle">Assigned Staff</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Notes */}
        {booking.notes && (
          <div className="bg-light-card dark:bg-dark-card rounded-xl p-5 shadow-sm">
            <h3 className="font-semibold text-sm mb-3 text-light-text dark:text-dark-text">Notes</h3>
            <p className="text-sm text-light-subtle dark:text-dark-subtle whitespace-pre-wrap">{booking.notes}</p>
          </div>
        )}

        {/* Action Buttons */}
        {showActions && (
          <div className="bg-light-card dark:bg-dark-card rounded-xl p-5 shadow-sm">
            <h3 className="font-semibold text-sm mb-4 text-light-text dark:text-dark-text">Update Status</h3>
            <div className="flex flex-wrap gap-3">
              {booking.status === 'pending' && (
                <>
                  <button
                    onClick={() => updateStatus('confirmed')}
                    disabled={updating}
                    className="flex items-center gap-2 px-5 py-3 rounded-xl font-semibold text-sm transition-all hover:opacity-90 disabled:opacity-50 text-white"
                    style={{ backgroundColor: '#3B82F6' }}
                  >
                    {updating ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle size={16} />}
                    Confirm Booking
                  </button>
                  <button
                    onClick={() => updateStatus('cancelled')}
                    disabled={updating}
                    className="flex items-center gap-2 px-5 py-3 rounded-xl font-semibold text-sm transition-all hover:opacity-90 disabled:opacity-50 text-white"
                    style={{ backgroundColor: '#EF4444' }}
                  >
                    {updating ? <Loader2 size={16} className="animate-spin" /> : <XCircle size={16} />}
                    Cancel Booking
                  </button>
                </>
              )}
              {booking.status === 'confirmed' && (
                <>
                  <button
                    onClick={() => updateStatus('completed')}
                    disabled={updating}
                    className="flex items-center gap-2 px-5 py-3 rounded-xl font-semibold text-sm transition-all hover:opacity-90 disabled:opacity-50 text-white"
                    style={{ backgroundColor: '#10B981' }}
                  >
                    {updating ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle size={16} />}
                    Mark as Completed
                  </button>
                  <button
                    onClick={() => updateStatus('cancelled')}
                    disabled={updating}
                    className="flex items-center gap-2 px-5 py-3 rounded-xl font-semibold text-sm transition-all hover:opacity-90 disabled:opacity-50 text-white"
                    style={{ backgroundColor: '#EF4444' }}
                  >
                    {updating ? <Loader2 size={16} className="animate-spin" /> : <XCircle size={16} />}
                    Cancel Booking
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BookingDetails;